// @flow
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import {
    Box,
    Button,
    Flex,
    Columns,
    Heading,
    Loader,
    Stack,
    Text,
    Icon,
    useToast
} from '@locus-taxy/neo-ui';

import { Input } from 'common/components/core/input';
import { ApiStatusEnum } from 'common/enums/API';
import { APIErrorScreen } from 'common/components/core/errors';
import useLatest from 'common/hooks/useLatest';
import useClientId from 'common/hooks/useClientId';
import FileUtils from 'common/utils/FileUtils';
import usePersonnel from 'common/hooks/usePersonnel';
import { platformEntitiesExportStatus as platformEntitiesExportStatusApi } from 'common/middleware/api';

import {
    serviceabilityUpload as serviceabilityUploadApi,
    serviceabilityTemplate as serviceabilityTemplateApi,
    serviceabilityExport as serviceabilityExportApi
} from 'platform/settings/modules/orchestration/api';
import Skeleton from 'platform/settings/modules/orchestration/components/Skeleton';
import { DataTable } from 'platform/settings/modules/orchestration/components/DataTable';
import UploadButton from 'platform/settings/modules/orchestration/components/UploadButton';

import useGetCarriers from './useGetCarriers';
import useAsyncExport from './useAsyncExport';

type Props = {
    translationPath: string
};

const Serviceability = ({ translationPath }: Props): React.Node => {
    const [searchTerm, setSearchTerm] = React.useState();
    const [hideActions, setHideActions] = React.useState(false);

    const { t } = useTranslation('platform');

    const clientId = useClientId();
    const { email } = usePersonnel();

    const { showToast, closeToast, closeAllToasts } = useToast();

    const carriersQuery = useGetCarriers();

    const initExport = useAsyncExport({
        exportApi: serviceabilityExportApi,
        exportStatusApi: platformEntitiesExportStatusApi,
        entityName: t(`${translationPath}.label`),
        onError: () => setHideActions(false),
        onSuccess: () => setHideActions(false),
        onCloseToast: () => setHideActions(false)
    });

    const data = React.useMemo(() => {
        if (!carriersQuery.data) {
            return [];
        }

        const { carriers } = carriersQuery.data;

        const carriersList = carriers.toJS().flatMap(carrier =>
            carrier.serviceTypes.map(({ id, name }) => ({
                carrierId: carrier.carrierId,
                carrierName: carrier.name,
                serviceTypeId: id,
                serviceTypeName: name
            }))
        );

        return !searchTerm
            ? carriersList
            : carriersList.filter(({ carrierName, serviceTypeName }) => {
                  return (
                      carrierName
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase()) ||
                      serviceTypeName
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase())
                  );
              });
    }, [carriersQuery.data, searchTerm]);

    const handleDownloadRef = useLatest(
        ({
            carrierId,
            serviceTypeId
        }: {
            carrierId: string,
            serviceTypeId: string
        }) => {
            setHideActions(true);

            const params = { carrierId, serviceTypeId };
            const requestBody = {
                emailAddresses: [{ emailAddress: email }]
            };

            initExport({ params, requestBody });
        }
    );

    const handleDownloadTemplateRef = useLatest(
        async ({
            carrierId,
            serviceTypeId
        }: {
            carrierId: string,
            serviceTypeId: string
        }) => {
            closeAllToasts();

            setHideActions(true);

            const inProgressToast = showToast({
                title: t(`${translationPath}.downloadTemplateInProgressTitle`),
                palette: 'primary',
                icon: <Loader color="blue.500" />,
                autoHideDuration: null
            });

            try {
                const response = await serviceabilityTemplateApi.fetch({
                    clientId,
                    carrierId,
                    serviceTypeId
                });

                const blob = await response.blob();

                FileUtils.download({
                    blob,
                    fileName: 'serviceability-template.csv'
                });

                closeToast(inProgressToast);
            } catch (error) {
                closeToast(inProgressToast);
                showToast({
                    title: t(`${translationPath}.downloadTemplateFailed`),
                    palette: 'danger',
                    icon: (
                        <Icon icon="alert_hex" size="xLarge" color="red.400" />
                    ),
                    content: error.message
                });
            }

            setHideActions(false);
        }
    );

    const handleUploadRef = useLatest(
        async ({
            file,
            carrierId,
            serviceTypeId
        }: {
            file: File,
            carrierId: string,
            serviceTypeId: string
        }) => {
            closeAllToasts();
            setHideActions(true);

            const inProgressToast = showToast({
                title: t(`${translationPath}.uploadInProgress`),
                palette: 'primary',
                icon: <Loader color="blue.500" />,
                autoHideDuration: null
            });

            try {
                const formData = new FormData();
                formData.append('file', file);

                const {
                    errorFileUrl,
                    errorRecordCount,
                    successRecordCount
                } = await serviceabilityUploadApi.post(
                    { clientId, carrierId, serviceTypeId },
                    formData
                );

                closeToast(inProgressToast);

                if (errorRecordCount > 0) {
                    const errorMessage = t(
                        `${translationPath}.uploadFailedWithErrors`
                    );
                    const errorRecords = t(`${translationPath}.record`, {
                        count: errorRecordCount
                    });
                    const errorTitle = `${errorRecordCount}  ${errorRecords} ${errorMessage}`;

                    const toast = showToast({
                        title: errorTitle,
                        palette: 'danger',
                        icon: (
                            <Icon
                                icon="alert_hex"
                                size="xLarge"
                                color="red.400"
                            />
                        ),
                        action: t('translations:view'),
                        onAction: () => {
                            closeToast(toast);
                            FileUtils.download({
                                url: errorFileUrl,
                                fileName: 'serviceability_error.txt'
                            });
                        }
                    });
                }

                if (successRecordCount > 0) {
                    const successRecords = t(`${translationPath}.record`, {
                        count: successRecordCount
                    });
                    const successMessage = t(
                        `${translationPath}.uploadSuccess`
                    );

                    const successTitle = `${successRecordCount} ${successRecords} ${successMessage}`;

                    showToast({
                        title: successTitle,
                        palette: 'primary',
                        icon: (
                            <Icon icon="bell" size="xLarge" color="blue.400" />
                        )
                    });
                }
            } catch (error) {
                closeToast(inProgressToast);

                const title = error.data?.isHardFailure
                    ? t(`${translationPath}.invalidTemplate`)
                    : t(`${translationPath}.uploadFailed`);

                showToast({
                    title,
                    palette: 'danger',
                    icon: (
                        <Icon icon="alert_hex" size="xLarge" color="red.400" />
                    ),
                    content: error.message
                });
            }

            setHideActions(false);
        }
    );

    const columns = React.useMemo(() => {
        return [
            {
                id: 'carrierName',
                accessor: 'carrierName',
                headerText: t(`${translationPath}.carrierName`)
            },
            {
                id: 'serviceTypeName',
                accessor: 'serviceTypeName',
                headerText: t(`${translationPath}.serviceType`)
            },
            {
                id: 'download',
                headerText: t('translations:download'),
                Cell: ({ row: { original } }) => {
                    const { carrierId, serviceTypeId } = original;

                    return (
                        <Button
                            icon="download"
                            appearance="ghost"
                            palette="primary"
                            size="small"
                            onClick={() => {
                                handleDownloadRef.current({
                                    carrierId,
                                    serviceTypeId
                                });
                            }}
                            disabled={hideActions}
                        >
                            {t('translations:download')}
                        </Button>
                    );
                }
            },
            {
                id: 'upload',
                headerText: t('translations:upload'),
                Cell: ({ row: { original } }) => {
                    const { carrierId, serviceTypeId } = original;

                    return (
                        <UploadButton
                            onUpload={file => {
                                handleUploadRef.current({
                                    file,
                                    carrierId,
                                    serviceTypeId
                                });
                            }}
                            icon="import"
                            disabled={hideActions}
                            text={t('translations:upload')}
                        />
                    );
                }
            },
            {
                id: 'downloadTemplate',
                headerText: t('translations:downloadTemplate'),
                Cell: ({ row: { original } }) => {
                    const { carrierId, serviceTypeId } = original;

                    return (
                        <Button
                            icon="download"
                            appearance="ghost"
                            palette="primary"
                            size="small"
                            onClick={() => {
                                handleDownloadTemplateRef.current({
                                    carrierId,
                                    serviceTypeId
                                });
                            }}
                            disabled={hideActions}
                        >
                            {t('translations:downloadTemplate')}
                        </Button>
                    );
                }
            }
        ];
    }, [
        handleDownloadRef,
        handleDownloadTemplateRef,
        handleUploadRef,
        hideActions,
        t,
        translationPath
    ]);

    const total = t('item', { count: data.length });

    return (
        <Stack space="medium">
            <Flex justifyContent="space-between" alignItems="center">
                <Columns space="small" alignY="center">
                    <Heading size="large">
                        {t(`${translationPath}.label`)}
                    </Heading>

                    {data.length > 0 && (
                        <Text size="intermediate" color="grey.600">
                            {`(${data.length} ${total})`}
                        </Text>
                    )}
                </Columns>

                <Box width="340px">
                    <Input
                        type="text"
                        icon="search"
                        placeholder={t('translations:search')}
                        onChange={debounce(setSearchTerm, 300)}
                    />
                </Box>
            </Flex>

            {carriersQuery.status === ApiStatusEnum.Loading ? (
                <Skeleton height={30} borderRadius={3} count={10} />
            ) : carriersQuery.status === ApiStatusEnum.Error ? (
                <Box height="350px">
                    <APIErrorScreen
                        actionLabel={t('retry')}
                        onActionClick={carriersQuery.refetch}
                        subtitle={t('genericDataFetchError')}
                    />
                </Box>
            ) : (
                <DataTable columns={columns} data={data} />
            )}
        </Stack>
    );
};

export default Serviceability;
